import React, { useEffect, useRef } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { FaUserMd, FaHeart, FaAward, FaStethoscope, FaUsers, FaClock, FaPercent, FaStar } from 'react-icons/fa';

const About = ({ t }) => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      const elements = sectionRef.current.querySelectorAll('.fade-in, .scroll-fade-up, .scroll-fade-left, .scroll-fade-right, .scroll-zoom, .scroll-rotate');
      elements.forEach(el => observer.observe(el));
    }

    return () => observer.disconnect();
  }, []);

  const stats = [
    { 
      icon: <FaUsers />, 
      number: '500+', 
      label: t.about.stats.patients,
      delay: '0.1s'
    },
    { 
      icon: <FaClock />, 
      number: '5+', 
      label: t.about.stats.experience,
      delay: '0.2s'
    },
    { 
      icon: <FaPercent />, 
      number: '95%', 
      label: t.about.stats.success,
      delay: '0.3s'
    },
    { 
      icon: <FaStar />, 
      number: '10+', 
      label: t.about.stats.specializations,
      delay: '0.4s'
    }
  ];

  const values = [
    {
      icon: <FaUserMd />,
      title: t.about.values.expertise,
      delay: '0.1s'
    },
    {
      icon: <FaHeart />,
      title: t.about.values.care,
      delay: '0.2s'
    },
    {
      icon: <FaAward />,
      title: t.about.values.recovery,
      delay: '0.3s'
    },
    {
      icon: <FaStethoscope />,
      title: t.about.values.approach,
      delay: '0.4s'
    }
  ];

  return (
    <section id="about" className="section-padding bg-light" ref={sectionRef}>
      <Container>
        <Row className="justify-content-center text-center mb-5">
          <Col lg={8}>
            <h2 className="section-title scroll-fade-up title-enhance">{t.about.title}</h2>
            <p className="section-subtitle scroll-fade-up fade-in-delay-1">{t.about.description}</p>
          </Col>
        </Row>

        <Row className="mb-5 stagger-animation">
          {stats.map((stat, index) => (
            <Col lg={3} md={6} key={index} className="mb-4">
              <div 
                className="stats-card scroll-zoom hover-lift hover-glow animate-bounce-in" 
                style={{ 
                  '--i': index,
                  animationDelay: stat.delay 
                }}
              >
                <div className="stats-icon mb-3 animate-heartbeat hover-rotate" style={{ fontSize: '2rem', color: 'var(--primary-color)' }}>
                  {stat.icon}
                </div>
                <div className="stats-number gradient-text animate-elastic">{stat.number}</div>
                <div className="stats-label scroll-fade-left">{stat.label}</div>
              </div>
            </Col>
          ))}
        </Row>

        <Row className="justify-content-center stagger-animation">
          {values.map((value, index) => (
            <Col lg={3} md={6} key={index} className="mb-4">
              <div 
                className="text-center scroll-rotate" 
                style={{ 
                  '--i': index,
                  animationDelay: value.delay 
                }}
              >
                <div 
                  className="mb-3 d-inline-flex align-items-center justify-content-center hover-bounce float-slow animate-shimmer"
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: 'var(--gradient-primary)',
                    color: 'white',
                    fontSize: '2rem'
                  }}
                >
                  {value.icon}
                </div>
                <h5 className="mb-0">{value.title}</h5>
              </div>
            </Col>
          ))}
        </Row>
      </Container>
    </section>
  );
};

export default About;